import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/StaffPage.css";
import API_URL from "../apiConfig";

function StaffList({ token }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newStaff, setNewStaff] = useState({ name: "", email: "", position: "" });
  const [userDialog, setUserDialog] = useState({ show: false, staffId: null, username: "", password: "" });
  const [pwDialog, setPwDialog] = useState({ show: false, staffId: null, password: "" });
  const [editId, setEditId] = useState(null);
  const [editStaff, setEditStaff] = useState({ name: "", email: "", position: "" });
  const [message, setMessage] = useState("");

  useEffect(() => {
    fetchStaff();
    // eslint-disable-next-line
  }, []);

  const fetchStaff = () => {
    setLoading(true);
    fetch(`${API_URL}/api/staff`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((res) => res.json())
      .then((data) => {
        setStaff(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      const res = await fetch(`${API_URL}/api/staff`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newStaff),
      });
      const data = await res.json();
      if (res.ok) {
        setShowAdd(false);
        setNewStaff({ name: "", email: "", position: "" });
        fetchStaff();
      } else {
        setMessage(data.error || "Error adding staff");
      }
    } catch {
      setMessage("Error adding staff");
    }
  };

  const onEdit = (member) => {
    setEditId(member.id);
    setEditStaff({ name: member.name, email: member.email, position: member.position || "" });
    setMessage("");
  };

  const handleEditStaff = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      const res = await fetch(`${API_URL}/api/staff/${editId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editStaff),
      });
      const data = await res.json();
      if (res.ok) {
        setEditId(null);
        setEditStaff({ name: "", email: "", position: "" });
        fetchStaff();
      } else {
        setMessage(data.error || "Error updating staff");
      }
    } catch {
      setMessage("Error updating staff");
    }
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm("Are you sure you want to delete this staff member?")) return;
    setMessage("");
    try {
      const res = await fetch(`${API_URL}/api/staff/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        fetchStaff();
      } else {
        setMessage(data.error || "Error deleting staff");
      }
    } catch {
      setMessage("Error deleting staff");
    }
  };

  // CREATE USER DIALOG
  const showUserDialogFn = (staffId) => setUserDialog({ show: true, staffId, username: "", password: "" });
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      const res = await fetch(`${API_URL}/api/staff/${userDialog.staffId}/create-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ username: userDialog.username, password: userDialog.password }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("User created!");
        setUserDialog({ show: false, staffId: null, username: "", password: "" });
        fetchStaff();
      } else {
        setMessage(data.error || "Error creating user");
      }
    } catch {
      setMessage("Error creating user");
    }
  };

  // UPDATE PASSWORD DIALOG
  const showPwDialogFn = (staffId) => setPwDialog({ show: true, staffId, password: "" });
  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    setMessage("");
    try {
      const res = await fetch(`${API_URL}/api/staff/${pwDialog.staffId}/update-password`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: pwDialog.password }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage("Password updated!");
        setPwDialog({ show: false, staffId: null, password: "" });
      } else {
        setMessage(data.error || "Error updating password");
      }
    } catch {
      setMessage("Error updating password");
    }
  };

  return (
    <div className="staff-list-container">
      <div className="staff-list-header">
        <h3>Staff List</h3>
        <button className="primary-btn" onClick={() => setShowAdd(true)}>+ Add Staff</button>
      </div>
      {message && <div className="staff-message">{message}</div>}
      {showAdd && (
        <form className="staff-add-form" onSubmit={handleAddStaff}>
          <input
            type="text"
            placeholder="Name"
            value={newStaff.name}
            onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={newStaff.email}
            onChange={(e) => setNewStaff({ ...newStaff, email: e.target.value })}
            required
          />
          <input
            type="text"
            placeholder="Position"
            value={newStaff.position}
            onChange={(e) => setNewStaff({ ...newStaff, position: e.target.value })}
          />
          <button type="submit" className="primary-btn">Add</button>
          <button type="button" className="cancel-btn" onClick={() => setShowAdd(false)}>Cancel</button>
        </form>
      )}
      {loading ? (
        <div>Loading staff...</div>
      ) : (
        <table className="staff-list-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Position</th>
              <th>Email</th>
              <th>User</th>
              <th>View</th>
              <th>Edit</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((member) =>
              editId === member.id ? (
                <tr key={member.id}>
                  <td>
                    <input
                      value={editStaff.name}
                      onChange={e => setEditStaff({ ...editStaff, name: e.target.value })}
                      required
                    />
                  </td>
                  <td>
                    <input
                      value={editStaff.position}
                      onChange={e => setEditStaff({ ...editStaff, position: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      value={editStaff.email}
                      onChange={e => setEditStaff({ ...editStaff, email: e.target.value })}
                      required
                    />
                  </td>
                  <td>
                    {member.username ? (
                      <span className="created-user">{member.username}</span>
                    ) : (
                      <button className="mini-btn" onClick={() => showUserDialogFn(member.id)}>Create User</button>
                    )}
                  </td>
                  <td>
                    <Link to={`/admin/manage-staff/${member.id}`}>View</Link>
                  </td>
                  <td>
                    <button className="mini-btn" onClick={handleEditStaff}>Save</button>
                    <button className="cancel-btn" onClick={() => setEditId(null)}>Cancel</button>
                  </td>
                  <td>
                    <button className="mini-btn" onClick={() => handleDeleteStaff(member.id)}>Delete</button>
                  </td>
                </tr>
              ) : (
                <tr key={member.id}>
                  <td>{member.name}</td>
                  <td>{member.position || "—"}</td>
                  <td>{member.email}</td>
                  <td>
                    {member.username ? (
                      <>
                        <span className="created-user">{member.username}</span>
                        <button className="mini-btn" style={{ marginLeft: 8 }} onClick={() => showPwDialogFn(member.id)}>Update Password</button>
                      </>
                    ) : (
                      <button className="mini-btn" onClick={() => showUserDialogFn(member.id)}>Create User</button>
                    )}
                  </td>
                  <td>
                    <Link to={`/admin/manage-staff/${member.id}`}>View</Link>
                  </td>
                  <td>
                    <button className="mini-btn" onClick={() => onEdit(member)}>Edit</button>
                  </td>
                  <td>
                    <button className="mini-btn" onClick={() => handleDeleteStaff(member.id)}>Delete</button>
                  </td>
                </tr>
              )
            )}
          </tbody>
        </table>
      )}

      {/* User create dialog */}
      {userDialog.show && (
        <div className="staff-user-dialog">
          <form onSubmit={handleCreateUser}>
            <div>
              <label>Username</label>
              <input
                value={userDialog.username}
                onChange={e => setUserDialog({ ...userDialog, username: e.target.value })}
                required
              />
            </div>
            <div>
              <label>Password</label>
              <input
                type="password"
                value={userDialog.password}
                onChange={e => setUserDialog({ ...userDialog, password: e.target.value })}
                required
              />
            </div>
            <button type="submit" className="primary-btn">Create</button>
            <button type="button" className="cancel-btn" onClick={() => setUserDialog({ show: false, staffId: null, username: "", password: "" })}>Cancel</button>
          </form>
        </div>
      )}

      {/* Update password dialog */}
      {pwDialog.show && (
        <div className="staff-user-dialog">
          <form onSubmit={handleUpdatePassword}>
            <div>
              <label>New Password</label>
              <input
                type="password"
                value={pwDialog.password}
                onChange={e => setPwDialog({ ...pwDialog, password: e.target.value })}
                required
              />
            </div>
            <button type="submit" className="primary-btn">Update</button>
            <button type="button" className="cancel-btn" onClick={() => setPwDialog({ show: false, staffId: null, password: "" })}>Cancel</button>
          </form>
        </div>
      )}
    </div>
  );
}

export default StaffList;